const m = require('mithril');
const prop = require('mithril/stream');
const masonry = require('../libs/masonry');
const dragscroll = require('../libs/dragscroll');
const Draggable = require('../libs/draggable');
const loaders = require('../layout/components/loaders');
const auth = require('../auth/models/session');
const edit = require('../layout/components/edit');
const pathHeader = require('../layout/components/pathHeader');
const moveEquipmentModal = require('../equipment/moveEquipmentModal');
const pointTd = require('../data/components/pointTd');
const columnForm = require('../data/components/columnModal');
const alarmsTable = require('../alarms/alarmsTable');
const addCustomEquipmentModal = require('./addCustomEquipmentModal');
const addRoomModal = require('../locations/addRoomModal');
const equipmentDiagram = require('./components/equipmentDiagram');
const editCustomEquipmentModal = require('./editCustomEquipmentModal');
const containsLocationModal = require('./components/containsLocationModal');
const diagramConfiguration = require('./components/diagram/diagramConfiguration');
const megaMenu = require('./components/megaMenu');
const copyCustomEquipmentModal = require('./copyCustomEquipmentModal');
const mapElement = require('../maps/mapElement');
const element = require('../dashboards/components/element');
const editColumnOnDataset = require('./libs/editColumnOnDataset');
const addColumnOnDataset = require('./libs/addColumnOnDataset');
const equipmentModel = require('../equipment/models/equipment');
const requestStatusHandler = require('../layout/components/requestStatusHandler');
const RequestStatus = require('../layout/enums/RequestStatus');
const propertiesViewerModal = require('./components/equipmentPropertiesViewerModal');
const addDatasetOrElementModal = require('../locations/components/addDatasetOrElementModal');
const flash = require('../components/flash');
const modal = require('../layout/components/modal');
const bindEquipmentModal = require('./bindEquipmentModal');
const rowActionDropdown = require('../components/rowActionDropdown');
const { getChildLocations, getGrandchildLocations, isValidLocation, LOCATION_CLASSES } = require('../maps/mapUtils');
const viewEventTracker = require('../events/viewEventTracker');
const ontologyFields = require('../../../../models/equipment/ontologyFields');
const serviceTaxonomy = require('../service/serviceTaxonomy');
const pageTitle = require('../layout/pageTitle');


var equipmentDetail = {};
var MAX_INLINE_BINDING_EXPORT_PREVIEW_BYTES = 1024 * 1024;

function mergeOntologyClassFields(classDataFromOntology, parentFieldsArray) {
  const mergedFields = ontologyFields.normalizeFieldDefinitions(
    [].concat(
      classDataFromOntology && classDataFromOntology.data && Array.isArray(classDataFromOntology.data.fields)
        ? classDataFromOntology.data.fields
        : [],
      Array.isArray(parentFieldsArray) ? parentFieldsArray : []
    )
  );

  if (!classDataFromOntology) {
    return { data: { fields: mergedFields } };
  }

  if (!classDataFromOntology.data || typeof classDataFromOntology.data !== 'object') {
    classDataFromOntology.data = {};
  }
  classDataFromOntology.data.fields = mergedFields;
  return classDataFromOntology;
}

function getDefinedEquipmentFields(record, equipmentFields) {
  return ontologyFields.normalizeFieldDefinitions(equipmentFields).filter(field => {
    const resolved = ontologyFields.readEquipmentFieldValue(record, field);
    return ontologyFields.isDisplayableFieldValue(resolved.value, field);
  });
}

function persistSortOrder(ctrl, request, failureMessage) {
  return loaders.requestWithLoader(request)
    .then(function (result) {
      return Promise.resolve(ctrl.init()).then(function () {
        return result;
      });
    })
    .catch(function (error) {
      console.error('Unable to save reordered equipment data:', error);
      if (typeof ctrl.showBindingExportFlash === 'function') {
        ctrl.showBindingExportFlash('error', failureMessage || 'Unable to save the new order. The latest saved order was reloaded.');
      }
      m.redraw();
      return Promise.resolve(ctrl.init())
        .catch(function (reloadError) {
          console.error('Unable to reload equipment after an order failure:', reloadError);
          return null;
        })
        .then(function () {
          return null;
        });
    });
}

equipmentDetail.oninit = function (vnode) {
  var ctrl = this;
  document.title = 'nimrod | Equipment';
  ctrl.equipmentRecord = prop();
  ctrl.diagram = prop();
  ctrl.redrawDiagram = prop(false);
  ctrl.location = prop({});
  ctrl.parentLocation = prop({});
  ctrl.grandparentLocation = prop({});


  ctrl.siblingLocations = prop([]);
  ctrl.thisLocations = prop([]);
  ctrl.newFeatureStyle = prop();
  ctrl.requestStatus = prop(RequestStatus.PENDING);
  ctrl.requestStatusCode = prop();
  ctrl.equipmentClassOntologyData = prop();
  ctrl.childLocations      = prop([]);
  ctrl.grandchildLocations = prop([]);
  ctrl.componentChildEquipment = prop([]);
  ctrl.componentTableSearch = prop('');
  ctrl.componentTableExpandedClasses = prop({});
  // For equipment page: whether to show the map at all
  ctrl.hasEquipmentGeojson = prop(false);
  // Accordion state for the map. In edit mode, keep it open so the map always shows
  ctrl.isMapOpen = prop(edit.vm.editing());
  // Track map fullscreen to hide overlapping UI
  ctrl.isMapFullscreen = prop(false);
  // Map state to mirror locationDetail behavior
  // Default to an equipment-centric view unless overridden
  ctrl.mapSelectedView = prop('HVAC Equipment');
  ctrl.previousMapSelectedView = prop('Overview');
  ctrl.equipmentLocations = prop([]);
  ctrl.bindingExportFlash = prop([]);
  ctrl.bindingExportFlashBg = prop('bg-green');
  ctrl.bindingExportFlashTimer = null;
  ctrl.templateWarningEditSessionShown = false;
  ctrl.megaMenuCloseSignal = prop(0);
  ctrl.equipmentActionMenuCloseSignal = prop(0);
  ctrl.closeMegaMenu = function () {
    ctrl.megaMenuCloseSignal(ctrl.megaMenuCloseSignal() + 1);
  };
  ctrl.closeEquipmentActionMenu = function () {
    ctrl.equipmentActionMenuCloseSignal(ctrl.equipmentActionMenuCloseSignal() + 1);
  };

  ctrl.computeFeatureGroups = function () {
    const loc = ctrl.location();
    if (!loc || !loc['@class']) return;
    // Use the same grouping as locationDetail, driven by equipmentLocations
    ctrl.childLocations(
      getChildLocations(loc, ctrl.equipmentLocations())
    );
    ctrl.grandchildLocations(
      getGrandchildLocations(loc, ctrl.equipmentLocations())
    );
  };

  // Fetch equipment coordinates like locationDetail does
  ctrl.getEquipmentLocations = async function () {
    if (!ctrl.location() || !ctrl.location()['@rid']) return ctrl.equipmentLocations([]);
    if (ctrl.mapSelectedView() === 'Overview') {
      return ctrl.equipmentLocations([]);
    }
    const siteRid = ctrl.location()['@rid'].slice(1);
    const selected = ctrl.mapSelectedView();
    const view = selected === 'Overview' ? 'Equipment' : selected;
    let response = await loaders.requestWithLoader({
      url: `/api/v1/site/${siteRid}/equipmentcoordinates/${view}`,
      method: 'GET',
      background: true
    });
    let normalizedRecords = response.map(r => { return { in: r } });
    ctrl.equipmentLocations(normalizedRecords);
  };


  ctrl.init = async function () {
    viewEventTracker.recordCurrentViewEvent();
    ctrl.equipmentRecordRid = m.route.param('id');
    const equipmentUrl = `/api/v1/equipment/${ctrl.equipmentRecordRid}`;
    
    try {
      const equipmentData = await loaders.requestWithLoader({
        loader: 'main',
        url: equipmentUrl,
        method: 'GET',
        extract: function(xhr) {
          const response = { 
              status: xhr.status,
              body: xhr.responseText ? JSON.parse(xhr.responseText) : null
          };
          if (xhr.status < 200 || xhr.status >= 300) {
              throw {response: response};
          }
          return response.body;
        }
      });

      equipmentData.data = equipmentData.data || {};
      ctrl.equipmentRecord(equipmentData);
      pageTitle.set(equipmentData, { fallback: 'Equipment' });
      ctrl.equipmentRecordPortValues = equipmentData.ports ? Object.values(equipmentData.ports) : [];
      ctrl.equipmentRecord().context = 'child';
      // Collect immediate component children with geometry for map rendering
      const componentChildren = (equipmentData.out_hasComponent || [])
        .map(edge => {
          const rec = edge.in;
          // Tag child record with its parent equipment rid for styling
          if (!rec.equipmentParentRid) rec.equipmentParentRid = equipmentData['@rid'];
          return rec;
        })
        .filter(rec => isValidLocation(rec));
      // Always include the current equipment itself if it has geometry
      const parentHasGeometry = isValidLocation(equipmentData);
      // Track explicit geojson presence to decide whether to show the map
      ctrl.hasEquipmentGeojson(Boolean(equipmentData.geojson));
      const mergedComponents = parentHasGeometry ? [equipmentData, ...componentChildren] : componentChildren;
      ctrl.componentChildEquipment(mergedComponents);
      
      const functionalPath = Array.isArray(equipmentData.path) ? equipmentData.path : [];
      const locationTraversalPath = Array.isArray(equipmentData.locationPath) && equipmentData.locationPath.length ? equipmentData.locationPath : functionalPath;
      if (!functionalPath.length && !locationTraversalPath.length) return;

      const annotatePathNames = (pathArray) => {
        if (!Array.isArray(pathArray)) return;
        pathArray.forEach(node => {
          if (node) node.name = node.name || node['@class'];
        });
      };

      annotatePathNames(functionalPath);
      if (locationTraversalPath !== functionalPath) annotatePathNames(locationTraversalPath);

      equipmentData.name = equipmentData.name || (functionalPath[0]?.['@class'] || locationTraversalPath[0]?.['@class']);
      const breadcrumbPath = (functionalPath.length ? functionalPath.slice() : locationTraversalPath.slice()).reverse();
      equipmentData.path = breadcrumbPath;
      pageTitle.set(equipmentData, { fallback: 'Equipment' });
      const locationPathForSearch = locationTraversalPath.slice().reverse();
      // Prefer explicit location link via in_isLocationOf (location → equipment)
      // Edges may provide only an @rid for the location; accept those and resolve via fetch
      const isLocationOfLinks = (equipmentData.in_isLocationOf || [])
        .map(e => (e && e.out) ? e.out : null)
        .filter(loc => loc && loc['@rid']);
      // Fallback secondary: legacy containsLocation relationship if present
      const legacyLinkedLocations = (equipmentData.out_containsLocation || [])
        .map(e => e.in)
        .filter(loc => loc && LOCATION_CLASSES.includes(loc['@class']));
      let locationRecord = null;
      if (isLocationOfLinks.length > 0) {
        // If classes are present, prefer the deepest; otherwise just use the first linked location
        const withClass = isLocationOfLinks.filter(l => l['@class']);
        if (withClass.length > 0) {
          withClass.sort((a, b) => LOCATION_CLASSES.indexOf(b['@class']) - LOCATION_CLASSES.indexOf(a['@class']));
          locationRecord = withClass[0];
        } else {
          locationRecord = isLocationOfLinks[0];
        }
      } else if (legacyLinkedLocations.length > 0) {
        legacyLinkedLocations.sort((a, b) => LOCATION_CLASSES.indexOf(b['@class']) - LOCATION_CLASSES.indexOf(a['@class']));
        locationRecord = legacyLinkedLocations[0];
      }
      // Otherwise choose the nearest ancestor that is a Location (Site/Building/Level/Room)
      if (!locationRecord) {
        for (let i = equipmentData.path.length - 2; i >= 0; i--) { // skip the equipment itself at the end
          const candidate = equipmentData.path[i];
          if (candidate && LOCATION_CLASSES.includes(candidate['@class'])) {
            locationRecord = candidate;
            break;
          }
        }
      }
      if (!locationRecord) {
        // No location ancestor found; abort map setup gracefully
        ctrl.location({});
        ctrl.parentLocation({});
        ctrl.grandparentLocation({});
      } else {
      const locatioinUrl = '/api/v1/site/' + locationRecord['@rid'].slice(1);
      const locationData = await loaders.requestWithLoader({
        loader: 'main',
        url: locatioinUrl,
        method: 'GET'
      });
      locationData.path.reverse();
      // All results are set at once in the proper order to ensure the layers overlay correctly
      ctrl.grandparentLocation(locationData.path[locationData.path.length - 3]);
      ctrl.parentLocation(locationData.path[locationData.path.length - 2]);
      ctrl.location(locationData);
      ctrl.thisLocations([ctrl.location()]);
      }

      // Choose map view (equipment-specific > location default > fallback)
      if (equipmentData.mapView) {
        ctrl.mapSelectedView(equipmentData.mapView);
      } else if (ctrl.location().defaultMapView) {
        ctrl.mapSelectedView(ctrl.location().defaultMapView);
      } else {
        ctrl.mapSelectedView('HVAC Equipment');
      }
      // Keep previous in sync to avoid an immediate duplicate fetch in onupdate
      ctrl.previousMapSelectedView(ctrl.mapSelectedView());

      ctrl.newFeatureStyle({
        color: '#ba2525', // Line color
        weight: 2,   // Line width
        opacity: 1, // Line opacity
        fillColor: '#222222', // Fill color
        fillOpacity: 1,
      });

      
      let inheretedClassData = equipmentModel.getInheretedClassData(equipmentData['@class']);
      let classDataFromOntology = inheretedClassData.foundEquipmentClass;
      let parentFieldsArray = inheretedClassData.parentFieldsArray;
      classDataFromOntology = mergeOntologyClassFields(classDataFromOntology, parentFieldsArray);
      ctrl.equipmentClassOntologyData(classDataFromOntology);
      

      // Defer map data loading until the map accordion is opened
      ctrl.requestStatus(RequestStatus.SUCCESS);
      if (ctrl.isMapOpen()) {
        await ctrl.loadMapFeatureData();
      }
      m.redraw();
        
    } catch (error) {
        console.error('Error fetching equipment data:', error);
        ctrl.requestStatusCode(error?.status || error?.response?.status);
        ctrl.requestStatus(RequestStatus.ERROR);
    }
  };

  // note: onupdate is defined at component scope below


  ctrl.getSiblingLocations = async function getSiblingLocations () {
    // Add sibling locations to the map
    let parentRecord = ctrl.parentLocation();
    let locationRecord = ctrl.location();
    if (!parentRecord || !parentRecord['@rid'] || !locationRecord || !locationRecord['@rid']) {
      ctrl.siblingLocations([]);
      return;
    }

    let parentData = await loaders.requestWithLoader({
      loader: 'main',
      method: 'GET',
      url: `/api/v1/site/${parentRecord['@rid'].slice(1)}`,
    })

    // Remove this record from the parent's children
    var locationRid = locationRecord['@rid'].toString();
    var siblings = Array.isArray(parentData?.out_hasPart)
      ? parentData.out_hasPart.filter(function(locationEdge) {
          return locationEdge &&
            locationEdge.in &&
            locationEdge.in['@rid'] &&
            locationEdge.in['@rid'].toString() !== locationRid;
        })
      : [];
    ctrl.siblingLocations(siblings);
  }

  ctrl.loadMapFeatureData = async function loadMapFeatureData () {
    ctrl.equipmentLocations([]);
    try {
      await ctrl.getSiblingLocations();
    } catch (error) {
      console.error('Error fetching sibling locations:', error);
      ctrl.siblingLocations([]);
    }
    await ctrl.getEquipmentLocations();
    ctrl.computeFeatureGroups();
  }


  ctrl.addEquipmentNodeAndInitPage = async function(newEquipment) {
    var newYposition, newXposition, newRecordCreated;
    return loaders.requestWithLoader({
      loader: 'main',
      url: `/api/v1/equipment/${newEquipment['@rid'].slice(1)}`,
      method: 'GET',
    })
    .then(result => {
      newRecordCreated = result;
      var equipmentItem = {
        category: newEquipment['@class'] == 'Room' ? 'Room' : 'Equipment', 
        class: result['@class'],
        key: newEquipment['@rid'],
        name: newEquipment['name'],
        leftArray: [],
        rightArray: [],
        topArray: [],
        bottomArray: [],
        centerArray: [],
        datasetsRids: (result.out_Contains || []).map(c => c.in['@rid']),
        data: result.data,
      };

      if (result.ports) {
        Object.values(result.ports).forEach(port => {
          if (port.position == 'left') {
            equipmentItem.leftArray.push({portColor: diagramConfiguration.findPortColor(port.class), portId: port.name, type: edit.vm.editing() ? port.type : false, category: port.class})
          }
          if (port.position == 'right') {
            equipmentItem.rightArray.push({portColor: diagramConfiguration.findPortColor(port.class), portId: port.name, type: edit.vm.editing() ? port.type : false, category: port.class})
          }
          if (port.position == 'top') {
            equipmentItem.topArray.push({portColor: diagramConfiguration.findPortColor(port.class), portId: port.name, type: edit.vm.editing() ? port.type : false, category: port.class})
          }
          if (port.position == 'bottom') {
            equipmentItem.bottomArray.push({portColor: diagramConfiguration.findPortColor(port.class), portId: port.name, type: edit.vm.editing() ? port.type : false, category: port.class})
          }
          if (port.position == 'center') {
            equipmentItem.centerArray.push({portColor: diagramConfiguration.findPortColor(port.class), portId: port.name, type: edit.vm.editing() ? port.type : false, category: port.class})
          }
        })
      }
      var rightmostNode = findRightmostNode(ctrl.diagram());
      var newPosition = rightmostNode ? rightmostNode.location : {x: -200, y: 0};
      newXposition = parseFloat(newPosition.x) + 200;
      newYposition = parseFloat(newPosition.y);
      equipmentItem.latlong = [newXposition, newYposition];
      
      ctrl.diagram().model.addNodeData(equipmentItem)

      let body = {};
      body.diagramData = {
        ...result.diagramData,
        [ctrl.equipmentRecord()['@rid']]: {
        loc: equipmentItem.latlong.join(' ')
      }}

      return loaders.requestWithLoader({
        loader: 'main',
        url: `/api/v1/${equipmentItem.category}/${equipmentItem.key.slice(1)}`,
        method: 'PUT',
        body
      })
    })
    .then(result => {
      let equipmentNode = ctrl.diagram().findNodeForKey(newEquipment['@rid']);
      newYposition = newYposition + equipmentNode.actualBounds.height + 20;
      (result.out_Contains || []).filter(containsEdge => !containsEdge.in.visible || containsEdge.in.visible != 'false').forEach(containsEdge => {
        let datasetItem = {
          category: 'Dataset',
          key: containsEdge.in['@rid'],
          name: containsEdge.in['name'],
          columns: containsEdge.in['columns'],
          out_Column: containsEdge.in['out_Column'],
          loc: newXposition.toFixed(2) + " " + newYposition.toFixed(2),
        };
      
        ctrl.diagram().model.addNodeData(datasetItem);
        newYposition = newYposition + datasetNode.actualBounds.height + 30;

        let body = {};
        body.diagramData = {[ctrl.equipmentRecord()['@rid']]: {
          loc: datasetItem.loc
        }}

        return loaders.requestWithLoader({
          loader: 'main',
          url: `/api/v1/${datasetItem.category}/${datasetItem.key.slice(1)}`,
          method: 'PUT',
          body
        })
      })
    })
    .then(r => {
      return ctrl.init();
    })
    .then(r  => {
      ctrl.redrawDiagram(true);
    });
  }

  ctrl.initAndRedrawDiagram = function() { 
    ctrl.init()
    .then(() => {
      ctrl.redrawDiagram(true)
    }) 
  } 

  ctrl.addDatasetNodeAndInitPage = async function () {
    await ctrl.init();
    ctrl.redrawDiagram(true);
    m.redraw();
  }

  ctrl.addEquipmentPortModal = async function () {
    await ctrl.init();
    ctrl.redrawDiagram(true);
    m.redraw();
  }

  ctrl.deleteCallback = function() {
    m.route.set(`/location/${ctrl.location()['@rid'].slice(1)}`);
  }

  ctrl.showBindingExportFlash = function (type, message) {
    if (ctrl.bindingExportFlashTimer) {
      clearTimeout(ctrl.bindingExportFlashTimer);
      ctrl.bindingExportFlashTimer = null;
    }
    ctrl.bindingExportFlashBg(type === 'error' ? 'bg-red' : 'bg-green');
    ctrl.bindingExportFlash(message ? [message] : []);
    ctrl.bindingExportFlashTimer = setTimeout(function () {
      ctrl.bindingExportFlash([]);
      ctrl.bindingExportFlashTimer = null;
      m.redraw();
    }, 2500);
  };

  ctrl.base64ToUint8Array = function (base64) {
    if (!base64) {
      throw new Error('No download file returned.');
    }
    var byteCharacters = window.atob(base64);
    var byteNumbers = new Array(byteCharacters.length);
    for (var i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Uint8Array(byteNumbers);
  };

  ctrl.decodeByteArrayToText = function (byteArray) {
    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder('utf-8').decode(byteArray);
    }
    var text = '';
    for (var i = 0; i < byteArray.length; i++) {
      text += String.fromCharCode(byteArray[i]);
    }
    return text;
  };

  ctrl.triggerBindingTemplateDownload = function (download, byteArray) {
    if (!download || (!download.base64 && !byteArray)) {
      throw new Error('No download file returned.');
    }
    var bytes = byteArray || ctrl.base64ToUint8Array(download.base64);
    var blob = new Blob([bytes], { type: download.contentType || 'application/json' });
    var link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = download.fileName || 'binding-template.json';
    document.body.appendChild(link);
    link.click();
    setTimeout(function () {
      window.URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    }, 0);
  };

  ctrl.openBindingExportPreviewModal = function (jsonText, download, payloadSize) {
    modal.vm.title('Export as Template');
    modal.vm.body = function () {
      return m('div', [
        m('div.f6.mid-gray.mb2', 'Export size: ' + payloadSize + ' bytes'),
        m('textarea.w-100.code.ba.b--mid-gray.pa2', {
          readonly: true,
          value: jsonText,
          style: {
            height: '60vh',
            resize: 'vertical'
          }
        })
      ]);
    };
    modal.vm.buttons([
      {
        label: 'Download',
        class: 'dib mr2',
        func: function () {
          ctrl.triggerBindingTemplateDownload(download);
        }
      }
    ]);
    modal.vm.open({ extrawidth: true });
  };

  ctrl.exportBindingTemplateAsJson = function () {
    var equipment = ctrl.equipmentRecord && ctrl.equipmentRecord ? ctrl.equipmentRecord() : null;
    var sourceEquipmentId = equipment && equipment['@rid'] ? equipment['@rid'].toString() : '';
    if (!sourceEquipmentId) {
      ctrl.showBindingExportFlash('error', 'Error');
      return;
    }

    loaders.requestWithLoader({
      loader: 'main',
      method: 'POST',
      url: '/api/v1/binding/run',
      body: {
        command: 'export-template',
        options: {
          sourceEquipmentId: sourceEquipmentId
        }
      }
    })
    .then(function (response) {
      if (!response || !response.download || !response.download.base64) {
        throw new Error('Binding export did not return a JSON file.');
      }

      var byteArray = ctrl.base64ToUint8Array(response.download.base64);
      var payloadSize = byteArray.length;
      hideMenu(ctrl);

      if (payloadSize <= MAX_INLINE_BINDING_EXPORT_PREVIEW_BYTES) {
        var previewText = ctrl.decodeByteArrayToText(byteArray);
        var formattedText = previewText;
        try {
          formattedText = JSON.stringify(JSON.parse(previewText), null, 2);
        } catch (e) {}
        ctrl.openBindingExportPreviewModal(formattedText, response.download, payloadSize);
      } else {
        ctrl.triggerBindingTemplateDownload(response.download, byteArray);
      }

      ctrl.showBindingExportFlash('success', 'Exported');
    })
    .catch(function () {
      ctrl.showBindingExportFlash('error', 'Error');
    });
  }


  ctrl.openBindEquipmentModal = function () {
    var equipment = ctrl.equipmentRecord && ctrl.equipmentRecord ? ctrl.equipmentRecord() : null;
    var classTemplates = [];
    try {
      var classRecord = equipment && equipment['@class'] ? equipmentModel.findClassData(equipment['@class']) : null;
      classTemplates = normalizeClassTemplates(classRecord && classRecord.data ? classRecord.data.templates : []);
      if (!classTemplates.length && equipment && equipment['@class']) {
        var inheritedClassData = equipmentModel.getInheretedClassData(equipment['@class']);
        classTemplates = normalizeClassTemplates(inheritedClassData && inheritedClassData.data ? inheritedClassData.data.templates : []);
      }
    } catch (error) {}
    classTemplates = classTemplates.filter(function (template) {
      return Boolean(template && template.equipmentRid);
    });
    bindEquipmentModal.open({
      equipment: equipment,
      classTemplates: classTemplates,
      formatClassTemplateLabel: formatClassTemplateLabel,
      base64ToUint8Array: ctrl.base64ToUint8Array,
      decodeByteArrayToText: ctrl.decodeByteArrayToText,
      hideMenu: function () {
        hideMenu(ctrl);
      },
      showBindingExportFlash: ctrl.showBindingExportFlash
    });
  };
  ctrl.openApplyBindingTemplateModal = ctrl.openBindEquipmentModal;

  ctrl.sortColumns = function(columns) {
    if (columns.length == 0) return Promise.resolve(null);
    var paretDatasetRid = columns[0].attributes.paretDatasetRid.value;
    var orderedColumns = columns.map((n, i) => {
      return {
        name: n.attributes.name.value,
        id: i
      };
    });

    return persistSortOrder(ctrl, {
      loader: 'background',
      url: `api/v1/dataset/${paretDatasetRid.slice(1)}`,
      method: 'PUT',
      body: {columns: orderedColumns}
    }, 'Unable to reorder columns. The latest saved order was reloaded.');
  };

  ctrl.openSetAsTemplateModal = function () {
    var equipment = ctrl.equipmentRecord && ctrl.equipmentRecord ? ctrl.equipmentRecord() : null;
    if (!equipment || !equipment['@rid'] || !equipment['@class']) {
      ctrl.showBindingExportFlash('error', 'Unable to set template');
      return;
    }

    hideMenu(ctrl);

    var existingTemplates = [];
    try {
      var classRecord = equipmentModel.findClassData(equipment['@class']);
      if (classRecord && classRecord.data && Array.isArray(classRecord.data.templates)) {
        existingTemplates = classRecord.data.templates;
      }
    } catch (error) {}

    var templateName = prop(buildDefaultTemplateName(equipment, existingTemplates));
    var templateError = prop('');
    var savingTemplate = prop(false);

    var submit = async function () {
      if (savingTemplate()) return;

      var name = String(templateName() || '').trim();
      if (!name) {
        templateError('Template name is required.');
        m.redraw();
        return;
      }

      templateError('');
      savingTemplate(true);
      m.redraw();

      try {
        await ctrl.setCurrentEquipmentAsTemplate(name);
        modal.vm.close();
        ctrl.showBindingExportFlash('success', `Added as template: ${name}`);
      } catch (error) {
        templateError(extractErrorMessage(error) || 'Unable to add as template.');
      } finally {
        savingTemplate(false);
        m.redraw();
      }
    };

    modal.vm.title('Add as Template');
    modal.vm.body = function () {
      return m('form', { role: 'form' }, [
        m('div', { class: 'form-group' }, [
          m('label.db.mb2', 'Template Name'),
          m('input.form-control.w-100', {
            type: 'text',
            value: templateName(),
            placeholder: 'Template name',
            oninput: function (ev) {
              templateName(ev.target.value);
            },
            onkeydown: function (ev) {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                submit();
              }
            }
          })
        ]),
        templateError()
          ? m('div.red.f6.mt2', templateError())
          : '',
        savingTemplate()
          ? m('div.gray.f6.mt2', 'Saving template...')
          : ''
      ]);
    };
    modal.vm.buttons([
      {
        label: 'Save',
        class: 'btn-good',
        func: submit
      }
    ]);
    modal.vm.open();
  };

  ctrl.openTemplateInUseInfoModal = function () {
    modal.vm.title('Template In Use');
    modal.vm.body = function () {
      return m('div.f4', 'You are about to edit equipment that is being used as a template.');
    };
    modal.vm.buttons([
      {
        label: 'Continue',
        class: 'btn-good',
        func: function () {
          modal.vm.close();
        }
      }
    ]);
    modal.vm.open();
  };

  ctrl.maybeShowTemplateInUseWarning = function () {
    if (ctrl.templateWarningEditSessionShown) return;

    var equipment = ctrl.equipmentRecord && ctrl.equipmentRecord ? ctrl.equipmentRecord() : null;
    if (!equipment || !equipment['@rid'] || !equipment['@class']) return;

    var equipmentRid = normalizeTemplateEquipmentRid(equipment['@rid'].toString());
    var classRecord = equipmentModel.findClassData(equipment['@class']);
    var templates = normalizeClassTemplates(classRecord && classRecord.data ? classRecord.data.templates : []);
    if (templates.length === 0) {
      var inheritedClassData = ctrl.equipmentClassOntologyData && ctrl.equipmentClassOntologyData ? ctrl.equipmentClassOntologyData() : null;
      templates = normalizeClassTemplates(inheritedClassData && inheritedClassData.data ? inheritedClassData.data.templates : []);
    }

    var isTemplateEquipment = templates.some(template => template.equipmentRid === equipmentRid);
    if (!isTemplateEquipment) return;

    ctrl.templateWarningEditSessionShown = true;
    ctrl.openTemplateInUseInfoModal();
  };

  ctrl.setCurrentEquipmentAsTemplate = async function (templateName) {
    var equipment = ctrl.equipmentRecord && ctrl.equipmentRecord ? ctrl.equipmentRecord() : null;
    var equipmentRid = normalizeTemplateEquipmentRid(equipment && equipment['@rid'] ? equipment['@rid'].toString() : '');
    var className = equipment && equipment['@class'] ? equipment['@class'] : '';
    var name = String(templateName || '').trim();

    if (!equipmentRid || !className) {
      throw new Error('Equipment record is unavailable.');
    }
    if (!name) {
      throw new Error('Template name is required.');
    }

    await equipmentModel.getClassTree();
    var classRecord = equipmentModel.findClassData(className);
    if (!classRecord || !classRecord.name) {
      throw new Error(`Unable to load class ${className}.`);
    }

    var classRecordToUpdate = JSON.parse(JSON.stringify(classRecord));
    classRecordToUpdate.data = classRecordToUpdate.data || {};
    var templates = normalizeClassTemplates(classRecordToUpdate.data.templates);

    if (templates.some(template => (template.name || '').toLowerCase() === name.toLowerCase())) {
      throw new Error(`Template name '${name}' already exists for class ${className}.`);
    }
    if (templates.some(template => template.equipmentRid === equipmentRid)) {
      throw new Error(`Template equipment @rid '${equipmentRid}' already exists for class ${className}.`);
    }

    templates.push({
      name: name,
      equipmentRid: equipmentRid
    });
    classRecordToUpdate.data.templates = templates;

    var payload = {
      name: classRecordToUpdate.name,
      parent: classRecordToUpdate.parent,
      data: classRecordToUpdate.data
    };

    await equipmentModel.saveChildrenClassData(convertToFormData(payload));
  };

  ctrl.sortDatasets = function(datasets) {  
    if (datasets.length == 0) return Promise.resolve(null);
    var parentEquipmentRid = ctrl.equipmentRecord()['@rid'];

    var rids = datasets.map(n => n.attributes.rid.value);
    return persistSortOrder(ctrl, {
      loader: 'background',
      method: 'PUT',
      url: '/api/v1/element/' + parentEquipmentRid.slice(1) + '/childorder',
      body: {rids: rids}
    }, 'Unable to reorder datasets. The latest saved order was reloaded.');
  };

  ctrl.sortChildEquipment = function(childEquipment) {  
    if (childEquipment.length == 0) return Promise.resolve(null);
    var parentEquipmentRid = ctrl.equipmentRecord()['@rid'];

    var rids = childEquipment.map(n => n.attributes.rid.value);
    return persistSortOrder(ctrl, {
      loader: 'background',
      method: 'PUT',
      url: '/api/v1/customequipment/' + parentEquipmentRid.slice(1) + '/childorder',
      body: {rids: rids}
    }, 'Unable to reorder child equipment. The latest saved order was reloaded.');
  };

  ctrl.sortChildDatasetsAndElements = function (datasetsAndElements) {
    if (datasetsAndElements.length == 0) return Promise.resolve(null);
    var parentEquipmentRid = ctrl.equipmentRecord()['@rid'];

    var rids = datasetsAndElements.map(n => n.attributes.rid.value);
    return persistSortOrder(ctrl, {
      loader: 'background',
      method: 'PUT',
      url: '/api/v1/customequipment/' + parentEquipmentRid.slice(1) + '/customorder',
      body: {rids: rids}
    }, 'Unable to reorder datasets and elements. The latest saved order was reloaded.');
  };

  ctrl.init();

};

function findRightmostNode(diagram) {
  var rightmostNode = null;
  if (!diagram || !diagram.nodes) return rightmostNode;
  diagram.nodes.each(function (node) {
    if (!rightmostNode || node.position.x > rightmostNode.position.x) {
      rightmostNode = node;
    }
    if (node.position.x == rightmostNode.position.x && node.position.y < rightmostNode.position.y) {
      rightmostNode = node;
    }
  });
  return rightmostNode;
}


function hideMenu(ctrl) {
}

function normalizeTemplateEquipmentRid(rid) {
  var trimmedRid = typeof rid === 'string' ? rid.trim() : '';
  if (!trimmedRid) return '';
  return trimmedRid.startsWith('#') ? trimmedRid : `#${trimmedRid}`;
}

function normalizeClassTemplates(templates) {
  if (!Array.isArray(templates)) return [];
  return templates.map(template => {
    var normalizedTemplate =
      template && typeof template === 'object'
        ? template
        : { name: typeof template === 'string' ? template : '' };

    return {
      name: typeof normalizedTemplate.name === 'string' ? normalizedTemplate.name.trim() : '',
      equipmentRid: normalizeTemplateEquipmentRid(normalizedTemplate.equipmentRid)
    };
  });
}

function formatClassTemplateLabel(template) {
  var normalized = template && typeof template === 'object' ? template : {};
  var name = typeof normalized.name === 'string' ? normalized.name.trim() : '';
  var equipmentRid = normalizeTemplateEquipmentRid(normalized.equipmentRid);
  var displayName = name || 'Default';
  return equipmentRid ? `${displayName} (Template ID ${equipmentRid})` : displayName;
}

function buildDefaultTemplateName(equipmentRecord, existingTemplates) {
  var baseName = (equipmentRecord && equipmentRecord.name ? equipmentRecord.name : equipmentRecord && equipmentRecord['@class'] ? equipmentRecord['@class'] : 'Template').trim();
  if (!baseName) baseName = 'Template';
  var existingNames = new Set(
    normalizeClassTemplates(existingTemplates).map(template => (template.name || '').toLowerCase())
  );
  if (!existingNames.has(baseName.toLowerCase())) {
    return baseName;
  }

  var suffix = 2;
  var candidate = `${baseName} ${suffix}`;
  while (existingNames.has(candidate.toLowerCase())) {
    suffix += 1;
    candidate = `${baseName} ${suffix}`;
  }
  return candidate;
}

function convertToFormData(obj) {
  const formData = new FormData();

  function appendFormData(data, rootKey = '') {
    if (data && typeof data === 'object' && !(data instanceof File)) {
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          if (rootKey !== '') {
            appendFormData(data[key], rootKey + '[' + key + ']');
          } else {
            appendFormData(data[key], key);
          }
        }
      }
    } else {
      formData.append(rootKey, data);
    }
  }

  appendFormData(obj);
  return formData;
}

function extractErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.message && typeof error.message === 'string') return error.message;
  if (error.error && typeof error.error === 'string') return error.error;
  return '';
}

equipmentDetail.onupdate = async function() {
  var ctrl = this;
  if (edit.vm.editing()) {
    ctrl.maybeShowTemplateInUseWarning();
  } else {
    ctrl.templateWarningEditSessionShown = false;
  }

  if (ctrl.equipmentRecordRid != m.route.param('id')) {
    ctrl.equipmentRecordRid = m.route.param('id');
    ctrl.diagram(undefined);
    ctrl.componentTableSearch('');
    ctrl.componentTableExpandedClasses({});
    ctrl.templateWarningEditSessionShown = false;
    ctrl.init();
  } else if (edit.vm.editing() && !ctrl.isMapOpen()) {
    // Entered edit mode: ensure map is visible and data loaded
    ctrl.isMapOpen(true);
    await ctrl.loadMapFeatureData();
    m.redraw();
  } else if (ctrl.isMapOpen() && ctrl.mapSelectedView && ctrl.mapSelectedView() !== ctrl.previousMapSelectedView()) {
    ctrl.previousMapSelectedView(ctrl.mapSelectedView());
    await ctrl.loadMapFeatureData();
    m.redraw();
  }
}

equipmentDetail.onremove = function () {
  var ctrl = this;
  if (ctrl && ctrl.bindingExportFlashTimer) {
    clearTimeout(ctrl.bindingExportFlashTimer);
    ctrl.bindingExportFlashTimer = null;
  }
}

equipmentDetail.view = function () {
  var ctrl = this;
  if (ctrl.requestStatus() !== RequestStatus.SUCCESS || !equipmentModel.equipmentClassTree.categories.length) {
    return m(requestStatusHandler, {
      requestStatus: ctrl.requestStatus,
      requestStatusCode: ctrl.requestStatusCode
    });
  }
  
  return m('div.mv3', [
    m(pathHeader, {
      ancestors: ctrl.equipmentRecord().path, 
      record: ctrl.equipmentRecord(),
      class: '.flex.justify-between.mh3.mb2.items-end',
    },[
      edit.vm.editing() && auth.user().isSuperuser ?
        m(rowActionDropdown, {
          closeSignal: ctrl.equipmentActionMenuCloseSignal(),
          onOpen: ctrl.closeMegaMenu,
          items: [
            {
              label: 'Edit Equipment',
              icon: 'ion-ios-compose',
              onclick: editCustomEquipmentModal.editDialogue.bind(editCustomEquipmentModal, {...ctrl.equipmentRecord()}, { submitCallback: ctrl.initAndRedrawDiagram, delete: true, deleteCallback: ctrl.deleteCallback })
            },
            {
              label: 'Edit Permissions',
              icon: 'ion-ios-people',
              onclick: function () {
                m.route.set(`/equipment/${ctrl.equipmentRecord()['@class']}/${ctrl.equipmentRecord()['@rid'].toString().slice(1)}/permissions`);
              }
            },
            {
              label: 'Set Location',
              icon: 'ion-android-exit',
              onclick: moveEquipmentModal.editDialogue.bind(moveEquipmentModal, ctrl.equipmentRecord(), { submitCallback: ctrl.init, type: 'location' })
            },
            {
              label: 'Set Parent Equipment',
              icon: 'ion-android-exit',
              onclick: moveEquipmentModal.editDialogue.bind(moveEquipmentModal, ctrl.equipmentRecord(), { submitCallback: ctrl.init, type: 'equipment' })
            },
            {
              label: 'Bind Equipment',
              icon: 'ion-link',
              onclick: function (event) {
                if (event) event.preventDefault();
                ctrl.openBindEquipmentModal();
              }
            },
            {
              label: 'Copy Equipment',
              icon: 'ion-ios-copy-outline',
              onclick: copyCustomEquipmentModal.editDialogue.bind(copyCustomEquipmentModal, ctrl.equipmentRecord(), { submitCallback: ctrl.init })
            },
            {
              label: 'Export as Template',
              icon: 'ion-android-open',
              onclick: function (event) {
                if (event) event.preventDefault();
                ctrl.exportBindingTemplateAsJson();
              }
            },
            {
              label: 'Add as Template',
              icon: 'ion-android-add-circle',
              onclick: function (event) {
                if (event) event.preventDefault();
                ctrl.openSetAsTemplateModal();
              }
            }
          ]
        })
        :
        m('div')
    ]),
    m(flash, { items: ctrl.bindingExportFlash(), bg: ctrl.bindingExportFlashBg() }),
    m(megaMenu, {
      equipmentRecord: ctrl.equipmentRecord,
      equipmentClassOntologyData: ctrl.equipmentClassOntologyData,
      closeSignal: ctrl.megaMenuCloseSignal(),
      onOpen: ctrl.closeEquipmentActionMenu,
      submitCallback: ctrl.init,
      submitCallbackAddingEquipment: ctrl.addEquipmentNodeAndInitPage,
      findRightmostNode: findRightmostNode,
      diagram: ctrl.diagram,
      initAndRedrawDiagram: ctrl.initAndRedrawDiagram
    }),
    ctrl.equipmentRecord().hideDiagram && ctrl.equipmentRecord().hideDiagram === 'true' ?
        '' 
    :
      m('div.mb3.mh3', {
        style: {
          position: 'relative',
          'min-height': '66vh',
          display: (ctrl.equipmentRecord()?.ports && Object.keys(ctrl.equipmentRecord().ports).length || ctrl.equipmentRecord().out_hasComponent || ctrl.equipmentRecord().out_containsLocation || edit.vm.editing()) ? 'block' : 'none'
        }
      }, [
        
        m(equipmentDiagram, {
          equipmentRecord: ctrl.equipmentRecord,
          parentReinitialize: ctrl.init,
          diagram: ctrl.diagram,
          redrawDiagram: ctrl.redrawDiagram,
          parentReinitializeAndRedraw: ctrl.initAndRedrawDiagram,
          enableEquipmentDatasetSelection: true
        })
      ]),
    m('div', { style: ctrl.isMapFullscreen() ? { display: 'none' } : {} }, [
      m(`div.cf${ edit.vm.editing() ? '.mt5' : '.mt2' }`, [
        edit.vm.editing() ? datasetsEditMode(ctrl) : datasetsBrowseMode(ctrl)
      ]),
      m('div', [
        edit.vm.editing() ? 
        m('div.w-100.w-100-ns.w-100-l.pv1.center.ph3', [
          m('div.ba.b--dashed.bw1.pa2.pointer', {
            onclick: function () {
              var equipment = ctrl.equipmentRecord && ctrl.equipmentRecord();
              if (!equipment || !equipment['@rid']) {
                return;
              }
              addDatasetOrElementModal.open({
                equipment: ctrl.equipmentRecord,
                submitCallback: ctrl.addDatasetNodeAndInitPage
              });
            }
          }, [
            m('div.flex.justify-center.blue', [
              m('i.ion-plus.mr2'),
              m('div', 'New Element')
            ])
          ])
        ])
        : 
          ''
      ]),
      equipmentBlocks(ctrl),
    ]),
    //Linked Locations
    m('div.cf', [
      ctrl.equipmentRecord().out_containsLocation && 
      Array.isArray(ctrl.equipmentRecord().out_containsLocation) &&
      ctrl.equipmentRecord().out_containsLocation.length > 0 ?
        m('div.bw1.pb2.f2.bb.mb1.mh3', 'Linked Locations')
      : 
        '',
      linkedLocationsTemplate(ctrl),
    ]),
    edit.vm.editing() ?
        m('div.flex', [
          m('div.w-100.w-50-ns.w-50-l.pv1.pr2.ph3   ',[
            m('div.ba.b--dashed.bw1.pa2', {
              onclick: containsLocationModal.editDialogue.bind(containsLocationModal, undefined, ctrl.equipmentRecord(), undefined, { submitCallback: ctrl.addEquipmentNodeAndInitPage })
            }, [
              m('div.flex.justify-center.blue', [
                m('i.ion-plus.mr2'),
                m('div', 'Link Location')
              ])
            ])
          ])
        ])
      : 
        '',
    (edit.vm.editing() || ctrl.hasEquipmentGeojson()) ?
      [
        (() => {
          const isOpen = edit.vm.editing() || ctrl.isMapOpen();
          const collapsedClass = 'ba b--mid-gray bw1';
          const openStyle = isOpen
            ? { borderTop: '1px solid #666', borderLeft: '2px dotted #666', borderRight: 'none', borderBottom: 'none', paddingBottom: '1rem' }
            : {};

          return m('div.mv3.mh3 mb1 ba b--mid-gray' + (edit.vm.editing() ? '.mt4' : ''), {
            class: isOpen ? 'ba b--mid-gray' : collapsedClass,
            style: openStyle
          }, [
            isOpen
              ? null
              : m('div.pointer.flex.items-center.justify-between.pv3.ph3.f2', {
                  role: 'button',
                  tabindex: 0,
                  'aria-expanded': true,
                  onclick: async () => {
                    if (edit.vm.editing()) return;
                    const wasOpen = ctrl.isMapOpen();
                    ctrl.isMapOpen(!wasOpen);
                    if (!wasOpen) {
                      await ctrl.loadMapFeatureData();
                      m.redraw();
                    }
                  },
                  onkeydown: async (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (edit.vm.editing()) return;
                      const wasOpen = ctrl.isMapOpen();
                      ctrl.isMapOpen(!wasOpen);
                      if (!wasOpen) {
                        await ctrl.loadMapFeatureData();
                        m.redraw();
                      }
                    }
                  }
                }, [
                  m('div', 'Location'),
                  edit.vm.editing() ? null : m('i.ion-ios-arrow-down.blue')
                ]),
            isOpen
              ? m('div.flex.flex-wrap.relative', { style: { height: '70vh', paddingTop: 0, marginTop: 0 } }, [
                  (!edit.vm.editing()
                    ? m('div.absolute.pointer', {
                        style: { top: '8px', right: '8px', zIndex: 500 },
                        onclick: async () => {
                          ctrl.isMapOpen(false);
                          m.redraw();
                        }
                      }, m('i.ion-close.blue.mr3'))
                    : null),
                  m(mapElement, {
                    location: ctrl.location,
                    parentLocation: ctrl.parentLocation,
                    grandparentLocation: ctrl.grandparentLocation,
                    equipmentRecord: ctrl.equipmentRecord,
                    childFeatures: () => [
                      ...(ctrl.childLocations() || []),
                      ...(ctrl.componentChildEquipment() || [])
                    ],
                    siblingFeatures: ctrl.siblingLocations,
                    grandchildFeatures: ctrl.grandchildLocations,
                    systemRecord: prop(),
                    mapSelectedView: ctrl.mapSelectedView,
                    focusRid: ctrl.equipmentRecord() && ctrl.equipmentRecord()['@rid'],
                    focusRidChildren: () => (ctrl.componentChildEquipment() || []).map(r => r['@rid']),
                    alwaysIncludeRids: () => [
                      ctrl.equipmentRecord() && ctrl.equipmentRecord()['@rid'],
                      ...((ctrl.componentChildEquipment() || []).map(r => r['@rid']))
                    ].filter(Boolean),
                    showPathOverlay: true,
                    onFullscreenChange: (isFull) => {
                      ctrl.isMapFullscreen(isFull);
                      m.redraw();
                    }
                  })
                ])
              : ''
          ]);
        })()
      ]
      :
      '',
    m('div.mh3',[
      m(alarmsTable, {
        equipmentAllRids: [{...ctrl.equipmentRecord()}],
        dashboardsAllRids: [],
        selectedView: prop(),
        location: prop({...ctrl.equipmentRecord()}),
      })
    ])
  ])
};

function equipmentBlocks (ctrl) {
  return m('div', [
    edit.vm.editing() ? equipmentTableMode(ctrl) : '',
  ])
}

function sortOnIndex (a,b) {
  return (a.index || 0) - (b.index || 0);
}

function equipmentTableMode (ctrl) {
  var componentRows = getComponentTableRows(ctrl);
  var query = (ctrl.componentTableSearch() || '').toString().toLowerCase().trim();
  var filteredRows = filterComponentRows(componentRows, query);
  var groupedRows = groupComponentRowsByClass(filteredRows);
  var maxBindingColumnCount = getMaxComponentBindingColumnCount(groupedRows);
  var searchActive = Boolean(query);

  return m('div.mh3.mv3', [
    m('div.flex.flex-wrap.items-center.justify-between.bb.bw1.pb2.mb2', [
      m('div.f2', 'Components'),
      componentRows.length > 6 ?
        m('span.flex.items-center.ba.b--blue.br2.ph1.h2.mv1', [
          m('i.ion-search.blue.f4.mr1'),
          m('input.bn.bg-transparent.mh1.mv1.w4', {
            placeholder: 'Search',
            value: ctrl.componentTableSearch(),
            oninput: function (ev) {
              ctrl.componentTableSearch(ev.target.value);
            },
            style: {
              outline: 'none'
            }
          }),
          ctrl.componentTableSearch() ?
            m('i.ion-close-circled.blue.f4.pointer.mr1', {
              onclick: function () {
                ctrl.componentTableSearch('');
              }
            })
          :
            ''
        ])
      :
        ''
    ]),
    filteredRows.length ?
      [
        m('div.mb3', {
          style: {
            maxWidth: '100%',
            overflow: 'hidden'
          }
        }, [
          m('div.dragscroll.pb3', {
            oncreate: dragscroll,
            onupdate: dragscroll,
            style: {
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'hidden'
            }
          }, [
            m('table.w-100.f4.narrow', {
              style: {
                width: '100%',
                minWidth: 'max-content'
              }
            }, buildComponentTableChildren(groupedRows, ctrl, {
              expandAll: searchActive,
              maxBindingColumnCount: maxBindingColumnCount
            }))
          ])
        ]),
        renderAddComponentButton(ctrl)
      ]
    :
      [
        m('div.moon-gray.f4.pv3', componentRows.length ? 'No components match the search.' : 'No child components.'),
        renderAddComponentButton(ctrl)
      ]
  ])
}

function filterComponentRows(componentRows, query) {
  var normalizedQuery = (query || '').toString().toLowerCase().trim();
  if (!normalizedQuery) return componentRows;
  return componentRows.filter(function (row) {
    return row.searchText.indexOf(normalizedQuery) !== -1;
  });
}

function getComponentTableRows(ctrl) {
  var parentRecord = ctrl.equipmentRecord();
  var componentEdges = (parentRecord.out_hasComponent || [])
    .slice()
    .filter(function (hasComponentEdge) {
      return hasComponentEdge && hasComponentEdge.in && !hasComponentEdge.in.deleted;
    });

  var ridMap = buildComponentRidMap(parentRecord, componentEdges);
  return componentEdges.map(function (hasComponentEdge, componentIndex) {
    var childEquipmentRecord = hasComponentEdge.in;
    var name = childEquipmentRecord.name || childEquipmentRecord['@class'] || '';
    var className = childEquipmentRecord['@class'] || '';
    var portsSummary = formatPortsSummary(childEquipmentRecord);
    var connectionsSummary = formatConnectionsSummary(childEquipmentRecord, parentRecord, ridMap);
    var datasets = getComponentDatasets(parentRecord, childEquipmentRecord);
    var bindingCells = getComponentBindingCells(datasets);
    var bindingCellsByKey = {};
    bindingCells.forEach(function (cell) {
      bindingCellsByKey[cell.key] = cell;
    });

    return {
      key: hasComponentEdge['@rid'] || childEquipmentRecord['@rid'] || ['component', className, name, componentIndex].join('-'),
      childEquipmentRecord: childEquipmentRecord,
      name: name,
      className: className,
      href: buildEquipmentHref(childEquipmentRecord),
      portsSummary: portsSummary,
      connectionsSummary: connectionsSummary,
      datasets: datasets,
      bindingCells: bindingCells,
      bindingCellsByKey: bindingCellsByKey,
      searchText: [
        name,
        className,
        portsSummary,
        connectionsSummary,
        bindingCells.map(formatBindingCellSearchText).join(' ')
      ].join(' ').toLowerCase()
    };
  });
}

function groupComponentRowsByClass(rows) {
  var groupsByClass = {};
  rows.forEach(function (row) {
    var className = row.className || 'Equipment';
    if (!groupsByClass[className]) {
      groupsByClass[className] = {
        className: className,
        rows: []
      };
    }
    groupsByClass[className].rows.push(row);
  });

  return Object.keys(groupsByClass)
    .sort(compareLabels)
    .map(function (className) {
      var group = groupsByClass[className];
      group.rows.sort(function (a, b) {
        return compareLabels(a.name || a.className, b.name || b.className);
      });
      group.bindingColumns = getComponentGroupBindingColumns(group.rows);
      return group;
  });
}

function buildComponentTableChildren(groupedRows, ctrl, options) {
  return buildComponentTableSections(groupedRows, ctrl, options);
}

function buildComponentTableSections(groupedRows, ctrl, options) {
  options = options || {};
  var maxBindingColumnCount = options.maxBindingColumnCount !== undefined
    ? options.maxBindingColumnCount
    : getMaxComponentBindingColumnCount(groupedRows);
  var sections = [];
  groupedRows.forEach(function (group) {
    var bindingColumns = group.bindingColumns || [];
    var expanded = options.expandAll || isComponentClassExpanded(ctrl, group.className);
    sections.push(m('tbody', { key: 'class-section-' + group.className }, [
      renderComponentClassGroupRow(group, ctrl, expanded, bindingColumns, maxBindingColumnCount)
    ]));
    if (expanded) {
      sections.push(m('tbody', { key: 'equipment-section-' + group.className }, [
        renderComponentGroupHeaderRow(group, bindingColumns, maxBindingColumnCount)
      ].concat(group.rows.map(function (row) {
        return renderComponentTableRow(row, ctrl, bindingColumns, maxBindingColumnCount);
      }))));
    }
  });
  return sections;
}

function isComponentClassExpanded(ctrl, className) {
  var expandedClasses = ctrl.componentTableExpandedClasses ? ctrl.componentTableExpandedClasses() : {};
  return Boolean(expandedClasses && expandedClasses[className]);
}

function toggleComponentClass(ctrl, className) {
  var expandedClasses = Object.assign({}, ctrl.componentTableExpandedClasses ? ctrl.componentTableExpandedClasses() : {});
  if (expandedClasses[className]) {
    delete expandedClasses[className];
  } else {
    expandedClasses[className] = true;
  }
  ctrl.componentTableExpandedClasses(expandedClasses);
}

function renderComponentClassGroupRow(group, ctrl, expanded, bindingColumns, maxBindingColumnCount) {
  return m('tr', { key: 'class-' + group.className }, [
    m('td.bb.bl-0.ph2.pv1.light-silver.f5', {
      colspan: getComponentGroupColspan(group, bindingColumns, maxBindingColumnCount),
      role: 'button',
      tabindex: 0,
      'aria-expanded': expanded,
      onclick: function () {
        toggleComponentClass(ctrl, group.className);
      },
      onkeydown: function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          toggleComponentClass(ctrl, group.className);
        }
      },
      style: {
        backgroundColor: '#3a3a3a',
        cursor: 'pointer',
        lineHeight: '1.1'
      }
    }, [
      m('i.mr2', { class: expanded ? 'ion-ios-arrow-down' : 'ion-ios-arrow-forward' }),
      group.className,
      m('span.light-silver.ml2', '(' + group.rows.length + ')')
    ])
  ]);
}

function renderComponentGroupHeaderRow(group, bindingColumns, maxBindingColumnCount) {
  bindingColumns = bindingColumns || group.bindingColumns || [];
  return m('tr', { key: 'header-' + group.className }, [
    m('th.bb.bl-0.bw1.tl.pl4.pr2.pv1.fw1.f5', 'Name')
  ].concat(bindingColumns.map(function (bindingColumn) {
    return m('th.bb.bl-0.bw1.tc.ph2.pv1.fw1.f5', [
      renderBindingColumnHeader(bindingColumn)
    ]);
  })).concat([
    m('th.bb.bl-0.bw1.tc.ph2.pv1.fw1.f5', {
      colspan: getComponentActionColspan(bindingColumns, maxBindingColumnCount)
    }, '')
  ]));
}

function renderBindingColumnHeader(bindingColumn) {
  var datasetLabel = bindingColumn.datasetName === 'Default' ? '' : bindingColumn.datasetName;
  return m('div.nowrap', [
    datasetLabel ? m('div.f6.moon-gray', datasetLabel) : '',
    m('div', bindingColumn.columnLabel)
  ]);
}

function getComponentRowActionItems(row, ctrl) {
  return [
    {
      label: 'Edit Equipment',
      icon: 'ion-ios-compose',
      onclick: editCustomEquipmentModal.editDialogue.bind(
        editCustomEquipmentModal,
        {...row.childEquipmentRecord},
        {
          submitCallback: ctrl.initAndRedrawDiagram,
          delete: true,
          deleteCallback: ctrl.initAndRedrawDiagram
        }
      )
    },
    {
      label: 'Open Equipment',
      icon: 'ion-ios-arrow-forward',
      href: row.href
    }
  ];
}

function renderComponentTableRow(row, ctrl, bindingColumns, maxBindingColumnCount) {
  return m('tr', { key: row.key }, [
    m('td.bb.bl-0.pl4.pr2.pv0.v-top.f5', [
      m('div.flex.items-center', { style: { minWidth: 0 } }, [
        m('a.blue.pointer.truncate.flex-auto', {
          href: row.href,
          title: row.name,
          style: { minWidth: 0 }
        }, row.name),
        m('span.ml2.flex-none', [
          m(rowActionDropdown, {
            items: getComponentRowActionItems(row, ctrl)
          })
        ])
      ])
    ])
  ].concat((bindingColumns || []).map(function (bindingColumn) {
    return renderComponentBindingCell(row, bindingColumn, ctrl);
  })).concat([
    m('td.bb.bl-0.ph2.pv0.v-top', {
      colspan: getComponentActionColspan(bindingColumns, maxBindingColumnCount)
    }, '')
  ]));
}

function getComponentGroupColspan(group, bindingColumns, maxBindingColumnCount) {
  var columns = bindingColumns || (group && group.bindingColumns) || [];
  return 2 + Math.max(maxBindingColumnCount || 0, columns.length);
}

function getComponentActionColspan(bindingColumns, maxBindingColumnCount) {
  var columnCount = (bindingColumns || []).length;
  var maxColumnCount = Math.max(maxBindingColumnCount || 0, columnCount);
  return 1 + (maxColumnCount - columnCount);
}

function getComponentDatasets(parentRecord, childEquipmentRecord) {
  return (childEquipmentRecord.out_Contains || [])
    .filter(function (containsEdge) {
      var record = containsEdge && containsEdge.in;
      return record && (
        record['@class'] === 'Dataset' ||
        Array.isArray(record.columns) ||
        Array.isArray(record.out_Column)
      );
    })
    .map(function (containsEdge) {
      var datasetRecord = Object.assign({}, containsEdge.in);
      datasetRecord.path = buildComponentDatasetPath(parentRecord, childEquipmentRecord, datasetRecord);
      return datasetRecord;
    });
}

function buildComponentDatasetPath(parentRecord, childEquipmentRecord, datasetRecord) {
  var parentPath = (parentRecord.path || []).map(toPathRecord);
  return parentPath.concat([
    toPathRecord(childEquipmentRecord),
    toPathRecord(datasetRecord)
  ]);
}

function toPathRecord(record) {
  return {
    '@rid': record && record['@rid'],
    name: record && (record.name || record['@class']),
    '@class': record && record['@class']
  };
}

function getComponentBindingCells(datasets) {
  var cells = [];
  (datasets || []).forEach(function (dataset) {
    getDatasetColumnDefinitions(dataset).forEach(function (column, columnIndex) {
      var columnRecord = findComponentColumnRecord(dataset, column, columnIndex);
      if (columnRecord) {
        columnRecord = Object.assign({}, columnRecord, {
          path: dataset.path.concat([toPathRecord(columnRecord)])
        });
      }
      cells.push({
        key: getBindingColumnKey(dataset, column, columnIndex),
        dataset: dataset,
        datasetName: getDatasetLabel(dataset),
        column: column,
        columnIndex: columnIndex,
        columnId: getColumnDefinitionId(column, columnIndex),
        columnLabel: getColumnDefinitionLabel(column),
        columnRecord: columnRecord
      });
    });
  });
  return cells;
}

function getDatasetColumnDefinitions(dataset) {
  var definitions = [];
  var columnKeys = {};

  function addDefinition(column, fallbackIndex) {
    var label = getColumnDefinitionLabel(column);
    if (!label) return;
    var key = label.toLowerCase();
    if (columnKeys[key]) return;
    columnKeys[key] = true;
    definitions.push(column && typeof column === 'object'
      ? column
      : {
        id: fallbackIndex,
        name: label
      });
  }

  if (Array.isArray(dataset.columns) && dataset.columns.length > 0) {
    dataset.columns.forEach(addDefinition);
    return definitions;
  }

  (dataset.out_Column || []).forEach(function (columnRecord, columnIndex) {
    addDefinition({
      id: columnRecord && columnRecord.index !== undefined ? columnRecord.index : columnIndex,
      name: getColumnEdgeLabel(columnRecord)
    }, columnIndex);
  });

  return definitions;
}

function findComponentColumnRecord(dataset, column, columnIndex) {
  var columnRecords = Array.isArray(dataset.out_Column) ? dataset.out_Column : [];
  var columnId = getColumnDefinitionId(column, columnIndex);
  var columnIndexValue = getColumnDefinitionIndex(column, columnIndex);
  var columnLabel = getColumnDefinitionLabel(column);
  if (columnIndexValue !== null) {
    var columnByIndex = columnRecords.find(function (columnRecord) {
      return columnRecord && columnRecord.index !== undefined && String(columnRecord.index) === String(columnIndexValue);
    });
    if (columnByIndex) return columnByIndex;
  }
  var columnByName = columnRecords.find(function (columnRecord) {
    return columnRecord && getColumnEdgeLabel(columnRecord) === columnLabel;
  });
  if (columnByName) return columnByName;
  var columnById = columnRecords.find(function (columnRecord) {
    return columnRecord && columnRecord.index !== undefined && String(columnRecord.index) === String(columnId);
  });
  return columnById;
}

function getComponentGroupBindingColumns(rows) {
  var bindingColumnsByKey = {};
  var bindingColumns = [];
  rows.forEach(function (row) {
    (row.bindingCells || []).forEach(function (cell) {
      if (!bindingColumnsByKey[cell.key]) {
        bindingColumnsByKey[cell.key] = true;
        bindingColumns.push({
          key: cell.key,
          datasetName: cell.datasetName,
          column: cell.column,
          columnIndex: cell.columnIndex,
          columnId: cell.columnId,
          columnLabel: cell.columnLabel
        });
      }
    });
  });
  return bindingColumns;
}

function getMaxComponentBindingColumnCount(groupedRows) {
  return (groupedRows || []).reduce(function (maxCount, group) {
    return Math.max(maxCount, ((group && group.bindingColumns) || []).length);
  }, 0);
}

function renderComponentBindingCell(row, bindingColumn, ctrl) {
  var cell = getComponentBindingCellForColumn(row, bindingColumn);
  if (!cell) {
    return m('td.bb.bl-0.tc.moon-gray.f5', '-');
  }
  if (cell.columnRecord && cell.columnRecord.in && cell.columnRecord.in['@rid']) {
    return m(pointTd, {
      columnRecord: cell.columnRecord,
      parentReinitialize: ctrl.addDatasetNodeAndInitPage,
      parentRid: row.childEquipmentRecord['@rid'],
      tdClass: '.bb.bl-0.tc',
      textAlign: 'center'
    });
  }
  return m('td.bb.bl-0.tc.f5.pointer', {
    title: 'Connect ' + cell.columnLabel,
    onclick: function () {
      openComponentBindingModal(cell, ctrl);
    }
  }, [
    m('i.ion-plus.blue.pointer')
  ]);
}

function getComponentBindingCellForColumn(row, bindingColumn) {
  if (!row || !bindingColumn) return null;
  var existingCell = row.bindingCellsByKey && row.bindingCellsByKey[bindingColumn.key];
  if (existingCell) return existingCell;

  var dataset = (row.datasets || []).find(function (candidateDataset) {
    return getDatasetLabel(candidateDataset) === bindingColumn.datasetName;
  });
  if (!dataset) return null;

  var column = findDatasetColumnDefinitionForBindingColumn(dataset, bindingColumn) || {
    id: bindingColumn.columnId,
    name: bindingColumn.columnLabel
  };
  var columnIndex = bindingColumn.columnIndex !== undefined && bindingColumn.columnIndex !== null
    ? bindingColumn.columnIndex
    : getColumnDefinitionId(column, bindingColumn.columnId);
  var columnId = getColumnDefinitionId(column, columnIndex);
  var columnRecord = findComponentColumnRecord(dataset, column, columnIndex);
  if (columnRecord) {
    columnRecord = Object.assign({}, columnRecord, {
      path: dataset.path.concat([toPathRecord(columnRecord)])
    });
  }

  return {
    key: bindingColumn.key,
    dataset: dataset,
    datasetName: getDatasetLabel(dataset),
    column: column,
    columnIndex: columnIndex,
    columnId: columnId,
    columnLabel: getColumnDefinitionLabel(column) || bindingColumn.columnLabel,
    columnRecord: columnRecord
  };
}

function findDatasetColumnDefinitionForBindingColumn(dataset, bindingColumn) {
  var definitions = getDatasetColumnDefinitions(dataset);
  return definitions.find(function (column, columnIndex) {
    var columnLabel = getColumnDefinitionLabel(column);
    var columnId = getColumnDefinitionId(column, columnIndex);
    return columnLabel === bindingColumn.columnLabel ||
      String(columnId) === String(bindingColumn.columnId);
  });
}

function openComponentBindingModal(cell, ctrl) {
  columnForm.editDialogue(
    cell.columnRecord && cell.columnRecord['@rid'] ? cell.columnRecord : {
      out: cell.dataset['@rid'],
      index: cell.columnId,
      path: cell.dataset.path
    },
    {
      columnName: cell.columnLabel,
      delete: true,
      submitCallback: ctrl.addDatasetNodeAndInitPage
    }
  );
}

function getBindingColumnKey(dataset, column, columnIndex) {
  return [
    getDatasetLabel(dataset),
    getColumnDefinitionLabel(column)
  ].join('::');
}

function getDatasetLabel(dataset) {
  return (dataset && (dataset.name || dataset['@class'])) || 'Dataset';
}

function getColumnDefinitionId(column, columnIndex) {
  if (column && typeof column === 'object' && column.id !== undefined && column.id !== null) {
    return column.id;
  }
  return columnIndex;
}

function getColumnDefinitionIndex(column, columnIndex) {
  if (column && typeof column === 'object' && column.index !== undefined && column.index !== null) {
    return column.index;
  }
  if (column && typeof column === 'object' && column.id !== undefined && column.id !== null && !Number.isNaN(Number(column.id))) {
    return column.id;
  }
  return null;
}

function getColumnDefinitionLabel(column) {
  if (typeof column === 'string') return normalizeDisplayLabel(column);
  return normalizeDisplayLabel(column && (column.name || column.columnName));
}

function getColumnEdgeLabel(columnRecord) {
  return normalizeDisplayLabel(columnRecord && (columnRecord.name || (columnRecord.in && columnRecord.in.name)));
}

function normalizeDisplayLabel(value) {
  if (value === undefined || value === null) return '';
  return value.toString().trim();
}

function formatBindingCellSearchText(cell) {
  var pointRecord = cell.columnRecord && cell.columnRecord.in;
  return [
    cell.datasetName,
    cell.columnLabel,
    pointRecord && (pointRecord.name || pointRecord['@class'])
  ].filter(Boolean).join(' ');
}

function renderAddComponentButton(ctrl) {
  return m('div.flex', [
    m('div.w-100.w-50-ns.w-50-l.pv1.pr2',[
      m('div.ba.b--dashed.bw1.pa2.pointer', {
        onclick: addCustomEquipmentModal.editDialogue.bind(addCustomEquipmentModal, undefined, undefined, ctrl.equipmentRecord(), { submitCallback: ctrl.addEquipmentNodeAndInitPage })
      }, [
        m('div.flex.justify-center.blue', [
          m('i.ion-plus.mr2'),
          m('div', 'Add Component')
        ])
      ])
    ])
  ]);
}

function compareLabels(a, b) {
  return (a || '').toString().localeCompare((b || '').toString(), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
}

function getChildDefinedFields(childEquipmentRecord) {
  let inheretedClassData = equipmentModel.getInheretedClassData(childEquipmentRecord['@class']);
  let classDataFromOntology = inheretedClassData.foundEquipmentClass;
  let parentFieldsArray = inheretedClassData.parentFieldsArray;
  classDataFromOntology = mergeOntologyClassFields(classDataFromOntology, parentFieldsArray);

  let equipmentFields = classDataFromOntology && classDataFromOntology.data ? classDataFromOntology.data.fields : [];
  return getDefinedEquipmentFields(childEquipmentRecord, equipmentFields);
}

function buildComponentRidMap(parentRecord, componentEdges) {
  var ridMap = {};
  function addRecord(record) {
    var rid = normalizeRid(record && record['@rid']);
    if (rid) ridMap[rid] = record;
  }

  addRecord(parentRecord);
  componentEdges.forEach(function (edge) {
    addRecord(edge && edge.in);
  });
  return ridMap;
}

function formatPortsSummary(record) {
  var ports = getEquipmentPorts(record);
  if (!ports.length) return '';

  var labels = ports.map(function (port) {
    var serviceClass = serviceTaxonomy.getServiceClass(port);
    return [port.name || ('Port ' + port.id), serviceClass].filter(Boolean).join(' ');
  });

  if (labels.length <= 3) return labels.join(', ');
  return labels.slice(0, 3).join(', ') + ' +' + (labels.length - 3);
}

function formatConnectionsSummary(childRecord, parentRecord, ridMap) {
  var entries = getConnectionEntries(childRecord, parentRecord, ridMap);
  if (!entries.length) return '';

  var labels = entries.map(function (entry) {
    return entry.label;
  });

  if (labels.length <= 2) return labels.join('; ');
  return labels.slice(0, 2).join('; ') + ' +' + (labels.length - 2);
}

function getConnectionEntries(childRecord, parentRecord, ridMap) {
  var childRid = normalizeRid(childRecord && childRecord['@rid']);
  var entries = [];
  var seen = {};

  function addEdge(edge, direction, otherRecordOrRid, portRecord, portId, fallbackServiceClass) {
    if (!edge) return;
    var edgeRid = normalizeRid(edge['@rid']) || [
      direction,
      normalizeRid(otherRecordOrRid),
      portId,
      fallbackServiceClass
    ].join(':');
    if (seen[edgeRid]) return;
    seen[edgeRid] = true;

    var serviceClass = serviceTaxonomy.getServiceClass(edge) || fallbackServiceClass || '';
    var portLabel = formatPortLabel(portRecord, portId);
    var otherLabel = getRecordLabel(otherRecordOrRid, parentRecord, ridMap);
    var arrow = direction === 'in' ? '<-' : '->';
    var label = [
      portLabel,
      arrow,
      otherLabel
    ].filter(Boolean).join(' ');
    if (serviceClass) label += ' (' + serviceClass + ')';
    entries.push({ label: label });
  }

  (childRecord.out_Feeds || []).forEach(function (edge) {
    addEdge(edge, 'out', edge.in, childRecord, edge.fromPortId);
  });

  (childRecord.out_Controls || []).forEach(function (edge) {
    addEdge(edge, 'out', edge.in, childRecord, edge.fromPortId, 'Controls');
  });

  (parentRecord.out_Feeds || []).forEach(function (edge) {
    if (normalizeRid(edge && edge.in) === childRid) {
      addEdge(edge, 'in', edge.out || parentRecord, childRecord, edge.toPortId);
    }
  });

  (parentRecord.in_Feeds || []).forEach(function (edge) {
    if (normalizeRid(edge && edge.out) === childRid) {
      addEdge(edge, 'out', edge.in || parentRecord, childRecord, edge.fromPortId);
    }
  });

  return entries;
}

function getEquipmentPorts(record) {
  return Object.values(record && record.ports ? record.ports : {}).sort(function (a, b) {
    var aName = (a.name || '').toString();
    var bName = (b.name || '').toString();
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  });
}

function formatPortLabel(record, portId) {
  var portIdString = normalizePortToken(portId);
  if (!portIdString) return '';
  if (portIdString.indexOf('Controls') === 0) return 'Controls';

  var port = findEquipmentPortByToken(record, portIdString);

  return port && port.name ? port.name : 'Port ' + portIdString;
}

function findEquipmentPortByToken(record, portToken) {
  var normalizedToken = normalizePortToken(portToken);
  if (!normalizedToken) return null;
  var unprefixedToken = normalizedToken.indexOf('Port_') === 0 ? normalizedToken.slice(5) : normalizedToken;

  return getEquipmentPorts(record).find(function (candidate) {
    if (!candidate) return false;
    return [
      candidate.id,
      candidate.name,
      candidate.portId,
      candidate.key
    ].some(function (value) {
      var candidateToken = normalizePortToken(value);
      if (!candidateToken) return false;
      return candidateToken === normalizedToken ||
        candidateToken === unprefixedToken ||
        'Port_' + candidateToken === normalizedToken;
    });
  }) || null;
}

function normalizePortToken(value) {
  if (value === undefined || value === null) return '';
  var token = value.toString().trim();
  var unprefixedToken = token.indexOf('Port_') === 0 ? token.slice(5).trim() : token;
  if (!unprefixedToken || unprefixedToken === 'undefined' || unprefixedToken === 'null') return '';
  return token;
}

function getRecordLabel(recordOrRid, parentRecord, ridMap) {
  if (recordOrRid && typeof recordOrRid === 'object') {
    return recordOrRid.name || recordOrRid['@class'] || normalizeRid(recordOrRid['@rid']);
  }

  var rid = normalizeRid(recordOrRid);
  if (!rid) return '';
  if (ridMap[rid]) return ridMap[rid].name || ridMap[rid]['@class'] || rid;

  var parentRid = normalizeRid(parentRecord && parentRecord['@rid']);
  if (parentRid && parentRid === rid) return parentRecord.name || parentRecord['@class'] || rid;
  return rid;
}

function normalizeRid(recordOrRid) {
  if (!recordOrRid) return '';
  var rid = typeof recordOrRid === 'object' ? recordOrRid['@rid'] : recordOrRid;
  if (!rid) return '';
  rid = rid.toString();
  return rid.charAt(0) === '#' ? rid : '#' + rid;
}

function buildEquipmentHref(record) {
  if (!record || !record['@rid'] || !record['@class']) return '#';
  return `/#!/equipment/${record['@class'].toLowerCase()}/${record['@rid'].slice(1)}`;
}

function equipmentEditModeMode (ctrl) {
  return m('div', {
    style: {
      height: 'auto',
    }
  },[
    m(Draggable, {
      onsort: ctrl.sortChildEquipment ,
      draggable: '.grid-item-equipment-drag', 
      handleClass:'.grid-item-equipment-drag-handle', 
      htmlElement: 'div.flex.w-100.flex-wrap', 
    },[
      (ctrl.equipmentRecord().out_hasComponent || []).sort(sortOnIndex).map(hasComponentEdge => {
        return equipmentTemplate(hasComponentEdge, ctrl)
      })
    ]),
    m('div.flex', [
      m('div.w-100.w-50-ns.w-50-l.pv1.pr2.ph3',[
        m('div.ba.b--dashed.bw1.pa2', {
          onclick: addCustomEquipmentModal.editDialogue.bind(addCustomEquipmentModal, undefined, undefined, ctrl.equipmentRecord(), { submitCallback: ctrl.addEquipmentNodeAndInitPage })
        }, [
          m('div.flex.justify-center.blue', [
            m('i.ion-plus.mr2'),
            m('div', 'Create and Add Equipment')
          ])
        ])
      ])
    ]),
  ])
}

function equipmentTemplate (hasComponentEdge, ctrl) {
  var childEquipmentRecord = hasComponentEdge.in;
  let inheretedClassData = equipmentModel.getInheretedClassData(childEquipmentRecord['@class']);
  let classDataFromOntology = inheretedClassData.foundEquipmentClass;
  let parentFieldsArray = inheretedClassData.parentFieldsArray;
  classDataFromOntology = mergeOntologyClassFields(classDataFromOntology, parentFieldsArray);
  
  let equipmentFields = classDataFromOntology && classDataFromOntology.data ? classDataFromOntology.data.fields : [];
  let definedFields = getDefinedEquipmentFields(childEquipmentRecord, equipmentFields);
  return m(`div.grid-item.w-100.w-50-ns.w-25-l.ph3.pv1.${edit.vm.editing() ? '.grid-item-equipment-drag' : ''}`, { 
    key: hasComponentEdge['@rid'], 
    rid: hasComponentEdge['@rid'] 
  }, [
    m('div.ba.bw1.pa2', [
      m(`div.mb2${edit.vm.editing() ? '.flex.justify-between' : ''}`, [
        edit.vm.editing() ?
          [
            m(`div.mb2.blue${edit.vm.editing() ? '.pointer.grid-item-equipment-drag-handle.truncate' : ''}`, {
              onclick: edit.vm.editing() ? editCustomEquipmentModal.editDialogue.bind(editCustomEquipmentModal, {...childEquipmentRecord}, { submitCallback: ctrl.initAndRedrawDiagram, delete: true, deleteCallback: ctrl.initAndRedrawDiagram }) : ''
            }, `${childEquipmentRecord.name ? childEquipmentRecord.name : childEquipmentRecord['@class']}`),
            m('a.blue.pointer', {
              href: `/#!/equipment/${childEquipmentRecord['@class'].toLowerCase()}/${childEquipmentRecord['@rid'].slice(1)}`
            }, [
              m('i.ion-ios-arrow-forward.ml2')
            ])
          ]
        :
          m('div.flex.justify-between', [
            m('a.mb2.blue.pointer', {
              href: `/#!/equipment/${childEquipmentRecord['@class'].toLowerCase()}/${childEquipmentRecord['@rid'].slice(1)}`
            }, [
              m('div.truncate', [
                `${childEquipmentRecord.name ? childEquipmentRecord.name : childEquipmentRecord['@class']}`
              ])
            ]),
            definedFields.length == 0 ? 
              '' 
            : 
              m('i.ion-ios-information-outline.white.f4.ml2.pointer', {
                onclick: () => {
                  propertiesViewerModal.editDialogue(childEquipmentRecord);
                }
              })
          ])
          
      ]),
      (childEquipmentRecord.out_Contains || [])
      .filter(containsEdge => edit.vm.editing() ? true : containsEdge.in.hideInParentEquipmentDetail != 'true')
      .map((containsEdge, idx) => {
        let dataset = containsEdge.in;
        dataset.path = [
          ...ctrl.equipmentRecord().path.map(path => {
            return {
              '@rid': path['@rid'],
              name: path.name || path['@class'],
              '@class': path['@class']
            }
          }), 
          { 
            '@rid': childEquipmentRecord['@rid'], 
            name: childEquipmentRecord.name || childEquipmentRecord['@class'], 
            '@class': childEquipmentRecord['@class'] 
          },
          { 
            '@rid': dataset['@rid'], 
            name: dataset.name || dataset['@class'], 
            '@class': dataset['@class'] 
          }
        ];

        ctrl.dataset = {...dataset};
        return m(`div.mb2${childEquipmentRecord.out_Contains && 
          childEquipmentRecord.out_Contains.length && 
          childEquipmentRecord.out_Contains.filter(containsEdge => containsEdge.in.hideInParentEquipmentDetail != 'true').length ==  idx + 1 ? '' : '.bb.b--blue'}`, [
          dataset.name == 'Default' ? '' : m('div.b1', dataset.name),
          m('table.w-100', {style: {tableLayout: 'fixed'}}, [
            (dataset.columns || []).map(function (column, jdx) {
              let foundColumn = findComponentColumnRecord(dataset, column, jdx);
              if (foundColumn) {
                foundColumn.path = [...dataset.path, {
                  '@rid': foundColumn['@rid'],
                  name: foundColumn.name || foundColumn['@class'],
                  '@class': foundColumn['@class']
                }];
              }
              return m('tr', [
                m(`td.f4.man.tl.moon-gray.w-50${dataset.columns && dataset.columns.length == jdx + 1 && !edit.vm.editing() ? '' : '.bb'}`, [
                  m(`div${edit.vm.editing() ? '.blue.pointer' : ''}`, {
                    onclick: edit.vm.editing() ? editColumnOnDataset.bind(null, column, jdx, dataset, foundColumn, {...ctrl, parentReinitialize: ctrl.addDatasetNodeAndInitPage}) : ''
                  }, column.name)
                ]),
                foundColumn ? 
                  m(pointTd, {
                    columnRecord: foundColumn, 
                    textAlign: 'center',
                    parentReinitialize: ctrl.addDatasetNodeAndInitPage,
                  }) 
                :
                  edit.vm.editing() ?
                    m(`td.tc.bb`, {
                      onclick: columnForm.editDialogue.bind(columnForm, {out: dataset['@rid'], index: column.id, path: ctrl.dataset.path}, {columnName: column.name, delete: true, submitCallback: ctrl.addDatasetNodeAndInitPage})
                    }, [
                      m('i.ion-plus.blue')
                    ]) 
                  : 
                    m(`td.tc${dataset.columns && dataset.columns.length == jdx + 1 ? '' : '.bb'}`)
              ])
            }),
            edit.vm.editing() ?
                m('tr', [
                  m('td.bb.bl-0.tc', [
                    addColumnOnDataset({
                      ...ctrl,
                      submitCallback: ctrl.addDatasetNodeAndInitPage
                    })
                  ])
                ])
              :
                '' 
          ])
        ])
      })
    ])
  ])
}

function datasetsBrowseMode(ctrl) {
      var datasets = (ctrl.equipmentRecord().out_Contains || [])
      var elements = (ctrl.equipmentRecord().out_hasElement || [])
      var datasetsAndElements = datasets.concat(elements)
      var datasetsAndElementsComponents = datasetsAndElements.sort((a, b) => a.index - b.index)
      .map(function (edge) {
        if (edge.in['@class'] === 'Element') {
          return m(element, {
            child:edge, 
            key: edge.in['@rid'], 
            dashboardCallback: ctrl.init,
            parentRid: ctrl.equipmentRecord()['@rid'],
            location: ctrl.equipmentRecord,
            expandedCloseIconMargin: 'mr3'
          })
        } else {
          let datasetRecord = edge.in;
          let fakeElement = {
            key: datasetRecord['@rid'],
            in: {
              '@rid': datasetRecord['@rid'],
              name: datasetRecord.name,
              type: datasetRecord.type,
              out_Contains: [ { ...edge } ]
            },
            permission: datasetRecord.permission
          }
          return m(element, { 
            key: datasetRecord['@rid'],
            child: fakeElement,
            dashboardCallback: ctrl.addDatasetNodeAndInitPage,
            datasetRepresentation: true,
            parentRid: ctrl.equipmentRecord()['@rid'],
            parentName: ctrl.equipmentRecord().name,
            expandedCloseIconMargin: 'mr3'
          })
        }
  });
  datasetsAndElementsComponents.unshift(m('div.grid-item.w-100.w-50-ns.w-25-l', { key: 0 })); //Fake div for get Masonry work
  datasetsAndElementsComponents.unshift(m('div.grid-sizer', { key: 1 }));
  return m('div.cf', { onupdate: masonry }, datasetsAndElementsComponents)
}

function datasetsEditMode(ctrl) {
  var datasets = (ctrl.equipmentRecord().out_Contains || [])
  var elements = (ctrl.equipmentRecord().out_hasElement || [])
  var datasetsAndElementsComponents = datasets.concat(elements)
  .sort((a, b) => a.index - b.index)
  .map(function (edge) {
    if (edge.in['@class'] === 'Element') {
      return m(element, {
        child:edge, 
        key: edge.in['@rid'], 
        dashboardCallback: ctrl.init,
        parentRid: ctrl.equipmentRecord()['@rid'],
        location: ctrl.equipmentRecord,
        expandedCloseIconMargin: 'mr3'
      })
    } else {
      let datasetRecord = edge.in;
      let fakeElement = {
        '@rid': edge['@rid'],
        in: {
          '@rid': datasetRecord['@rid'],
          name: datasetRecord.name,
          type: datasetRecord.type,
          out_Contains: [ { ...edge } ]
        }
      }
      return m(element, { 
        key: datasetRecord['@rid'],
        child: fakeElement,
        datasetRepresentation: true,
        dashboardCallback: ctrl.addDatasetNodeAndInitPage,
        parentName: ctrl.equipmentRecord().name,
        expandedCloseIconMargin: 'mr3'
      })
    }

  });

  datasetsAndElementsComponents.unshift(m('div.grid-sizer',{ key: 0 }));
  
  return m(Draggable, {onsort: ctrl.sortChildDatasetsAndElements, draggable: '.grid-item', handleClass:'.grid-item-drag-handle'}, datasetsAndElementsComponents)

}

function linkedLocationsTemplate(ctrl) {
  var linkedLocations = ctrl.equipmentRecord().out_containsLocation || [];

  return linkedLocations.map(function (containsLocationEdge) {
    let locationRecord = containsLocationEdge.in;
    let parentLocation = locationRecord.in_hasPart?.[0]?.in; // Use optional chaining to avoid errors
    return m('div.w-100.w-50-ns.w-25-l.pr2.pv1.ph3.fl', [
      m('div.ba.bw1.pa2', [
        m('div', [
          edit.vm.editing()
            ? m('div.blue',
                [
                  m('div.flex.justify-between.blue', [
                    m('div', `Edit ${locationRecord.name} ${locationRecord['@class'] == 'Room' ?  `- ${locationRecord.roomNumber}`: ''}`),
                    m('div.flex', [
                      m('i.ion-ios-compose.mr2', {
                        onclick: addRoomModal.editDialogue.bind(
                          addRoomModal,
                          locationRecord,
                          parentLocation,
                          { submitCallback: ctrl.initAndRedrawDiagram }
                        ),
                      }),
                      m('i.ion-ios-trash.red', {
                        onclick: containsLocationModal.editDialogue.bind(
                          containsLocationModal,
                          containsLocationEdge,
                          ctrl.equipmentRecord(),
                          locationRecord,
                          { submitCallback: ctrl.initAndRedrawDiagram }
                        ),
                      })
                    ])
                  ]),
                ]
              )
            : m("div", [
                m(
                  "a",
                  { href: `/#!/location/${locationRecord["@rid"].slice(1)}` },
                  `${locationRecord.name} ${locationRecord['@class'] == 'Room' ?  `- ${locationRecord.roomNumber}`: ''}`
                ),
              ]),
        ]),
      ]),
    ]);
  });
}

equipmentDetail._private = {
  equipmentTableMode,
  filterComponentRows,
  getComponentTableRows,
  groupComponentRowsByClass,
  buildComponentTableChildren,
  buildComponentTableSections,
  isComponentClassExpanded,
  toggleComponentClass,
  renderComponentTableRow,
  getComponentBindingCells,
  getDatasetColumnDefinitions,
  findComponentColumnRecord,
  getColumnDefinitionLabel,
  getColumnEdgeLabel,
  getComponentGroupBindingColumns,
  formatPortsSummary,
  formatConnectionsSummary,
  getConnectionEntries,
  getEquipmentPorts,
  formatPortLabel,
  findEquipmentPortByToken,
  normalizePortToken,
  persistSortOrder,
  datasetsBrowseMode,
  datasetsEditMode
};

module.exports = equipmentDetail;
